// "Subir a Mercado Libre": elegir publicación y color, diagnosticar la portada y subir.
// Todo lo hace atlas-backend con el token de la conexión; acá sólo se pide y se sigue el
// job (ADR-2026-09-28-mercadolibre-subir-fotos-sin-borrar-nada, en atlas-backend).

import { atlasBackendFetch, atlasBackendGet, pollJobUntilSettled } from "./atlasBackend";
import { codigoDeError, ErrorDeMercadoLibre } from "./mercadoLibreConexionLogica";
import type { DiagnosticoDeFoto, PublicacionParaSubir, ResultadoDeSubida } from "./mercadoLibreFotosLogica";

async function leerJson(r: Response): Promise<unknown> {
  try {
    return await r.json();
  } catch {
    return null;
  }
}

async function pedir<T>(fn: () => Promise<Response>): Promise<T> {
  let r: Response;
  try {
    r = await fn();
  } catch {
    throw new ErrorDeMercadoLibre("red");
  }
  const cuerpo = await leerJson(r);
  if (!r.ok) {
    const e = new ErrorDeMercadoLibre(codigoDeError(r.status, cuerpo));
    // Los bloqueos de la publicación ("es de catálogo"…) ya vienen en castellano.
    const msg = (cuerpo as { message?: unknown } | null)?.message;
    if (["publicacion_bloqueada", "publicacion_no_encontrada", "imagen_no_encontrada"].includes(e.code) && typeof msg === "string") {
      e.message = msg;
    }
    throw e;
  }
  return cuerpo as T;
}

function conMarca(brandId: string | null, q: Record<string, string>): string {
  const p = new URLSearchParams(q);
  if (brandId) p.set("brand_id", brandId);
  return p.toString();
}

export async function describirPublicacionDeMl(brandId: string | null, itemId: string): Promise<PublicacionParaSubir> {
  const r = await pedir<{ publicacion: PublicacionParaSubir }>(() =>
    atlasBackendGet(`/v1/mercadolibre/publicacion?${conMarca(brandId, { item_id: itemId })}`),
  );
  return r.publicacion;
}

/** A qué publicación y color va una foto de este producto, si Atlas lo sabe. */
export async function sugerenciaDeDestino(
  brandId: string | null,
  varianteId: string,
): Promise<{ item_id: string | null; variation_id: string | null }> {
  try {
    return await pedir(() => atlasBackendGet(`/v1/mercadolibre/sugerencia?${conMarca(brandId, { variante_id: varianteId })}`));
  } catch {
    return { item_id: null, variation_id: null };
  }
}

export async function diagnosticarFotoEnMl(
  brandId: string | null,
  p: { itemId: string; imagenId: string; posicion: "portada" | "secundaria" },
): Promise<DiagnosticoDeFoto> {
  return pedir(() =>
    atlasBackendFetch("/v1/mercadolibre/diagnosticar-foto", {
      brand_id: brandId,
      item_id: p.itemId,
      imagen_generada_id: p.imagenId,
      posicion: p.posicion,
    }),
  );
}

/** El id de la ejecución, o null si esa foto ya estaba subida (o subiéndose) a esa publicación. */
export async function subirFotoAMl(
  brandId: string | null,
  p: { itemId: string; imagenId: string; posicion: "portada" | "secundaria"; variationId: string | null; forzarPortada: boolean },
): Promise<string | null> {
  return subirFotosAMl(brandId, {
    itemId: p.itemId,
    fotos: [{ imagenId: p.imagenId, posicion: p.posicion }],
    variationId: p.variationId,
    forzarPortada: p.forzarPortada,
  });
}

/**
 * Varias fotos a la misma publicación, en un solo job (hasta 10; una sola portada). Devuelve null si
 * todas ya estaban subidas (o subiéndose) a esa publicación: el backend no encola nada.
 */
export async function subirFotosAMl(
  brandId: string | null,
  p: {
    itemId: string;
    fotos: Array<{ imagenId: string; posicion: "portada" | "secundaria" }>;
    variationId: string | null;
    forzarPortada: boolean;
  },
): Promise<string | null> {
  const r = await pedir<{ execution_id?: string | null; ya_estaban?: number }>(() =>
    atlasBackendFetch("/v1/mercadolibre/exportar-fotos", {
      brand_id: brandId,
      item_id: p.itemId,
      variation_id: p.variationId,
      fotos: p.fotos.map((f) => ({ imagen_generada_id: f.imagenId, posicion: f.posicion })),
      forzar_portada: p.forzarPortada,
    }),
  );
  if (r.execution_id) return r.execution_id;
  if ((r.ya_estaban ?? 0) > 0) return null;
  throw new ErrorDeMercadoLibre("error");
}

export async function esperarSubidaAMl(executionId: string, signal?: AbortSignal): Promise<ResultadoDeSubida | null> {
  const r = await pollJobUntilSettled(executionId, { timeoutMs: 4 * 60_000, intervalMs: 2500, signal });
  if (!r) return null;
  if (r.status === "failed") {
    const e = new ErrorDeMercadoLibre(r.errorType === "auth" ? "reconectar" : "error");
    if (r.userMessage) e.message = r.userMessage;
    throw e;
  }
  return (r.output ?? null) as ResultadoDeSubida | null;
}
